// ===================================================================
// Time & Labor — Dashboard
// Aggregate stats, recent entries, hours by employee
// ===================================================================

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Table, Button, Group, Badge, Stack,
  LoadingOverlay,
} from '@mantine/core';
import {
  IconUsers, IconClock, IconCurrencyDollar, IconFileText,
  IconPlus, IconShieldCheck, IconChecklist,
} from '@tabler/icons-react';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { checkEligibility, summarizeFlags } from '../../lib/eligibility';

const TEAL = '#0D9488';

function pf(v: string | undefined): number {
  return parseFloat(stripCurrency(v || '')) || 0;
}

export default function TimeLaborDashboard() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('timelabor');
  const activeIncident = useAuthStore(s => s.activeIncident);

  const {
    employees, loadingEmployees, fetchEmployees,
    laborRecords, loadingRecords, fetchLaborRecords,
  } = useTimeLaborStore();

  const { projects, fetchProjects } = usePAGrantsStore();

  useEffect(() => {
    fetchEmployees();
    fetchLaborRecords();
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter records by active incident
  const allRecords = useMemo(() => {
    if (!activeIncident) return laborRecords;
    return laborRecords.filter(r => !r.incidentId || r.incidentId === String(activeIncident.id));
  }, [laborRecords, activeIncident]);

  // Stats: employees with labor profiles
  const profileCount = employees.filter(e => e.status === 'active').length;

  // Pending review count
  const pendingReviewCount = useMemo(
    () => allRecords.filter(r => r.timesheetOnFile === 'submitted').length,
    [allRecords]
  );

  const totalHours = useMemo(() =>
    allRecords.reduce((sum, r) => sum + pf(r.hoursRegular) + pf(r.hoursOvertime), 0),
    [allRecords]
  );

  const totalCost = useMemo(() =>
    allRecords.reduce((sum, r) => sum + pf(r.totalCost), 0),
    [allRecords]
  );

  // Project lookup
  const projectLookup = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach(p => m.set(String(p.Rid), p.projectTitle || p.projectNumber || `#${p.Rid}`));
    return m;
  }, [projects]);

  // Recent entries (last 20)
  const recentEntries = useMemo(() =>
    [...allRecords].sort((a, b) => (b.workDate || '').localeCompare(a.workDate || '')).slice(0, 20),
    [allRecords]
  );

  // Hours by employee (all time, top 15)
  const hoursByEmployee = useMemo(() => {
    const map = new Map<string, number>();
    allRecords.forEach(r => {
      const name = r.employeeName || 'Unknown';
      map.set(name, (map.get(name) || 0) + pf(r.hoursRegular) + pf(r.hoursOvertime));
    });
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);
  }, [allRecords]);

  const maxHours = Math.max(...hoursByEmployee.map(e => e[1]), 1);

  // Build employee name -> profile map for eligibility checks
  const profileByName = useMemo(() => {
    const m = new Map<string, typeof employees[0]>();
    employees.forEach(e => { if (e.employeeName) m.set(e.employeeName, e); });
    return m;
  }, [employees]);

  // Compliance alerts
  const complianceData = useMemo(() => {
    const entries = allRecords.map(rec => {
      const profile = profileByName.get(rec.employeeName);
      if (!profile) return { flags: [] };
      return {
        flags: checkEligibility(
          profile.budgetStatus,
          profile.flsaStatus,
          profile.jobTitle || rec.employeeTitle,
          rec.category || '',
          pf(rec.hoursRegular),
          pf(rec.hoursOvertime),
        ),
      };
    });
    return summarizeFlags(entries);
  }, [allRecords, profileByName]);

  const loading = loadingEmployees || loadingRecords;

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Time & Labor</Title>
          <Text size="sm" c="dimmed">Force account labor tracking and cost aggregation</Text>
        </div>
        {canEdit && (
          <Button leftSection={<IconPlus size={14} />} color={TEAL} onClick={() => navigate('/timelabor/timeentry')}>
            New Time Entry
          </Button>
        )}
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }} mb="lg">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${TEAL}` }}>
          <IconUsers size={20} color={TEAL} />
          <Text size="xl" fw={700}>{profileCount}</Text>
          <Text size="xs" c="dimmed">Employees with Profiles</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #2563EB' }}>
          <IconClock size={20} color="#2563EB" />
          <Text size="xl" fw={700}>{totalHours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</Text>
          <Text size="xs" c="dimmed">Total Hours (All Time)</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <IconCurrencyDollar size={20} color="#059669" />
          <Text size="xl" fw={700}>{formatCurrency(totalCost.toFixed(2))}</Text>
          <Text size="xs" c="dimmed">Total Labor Cost (All Time)</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
          <IconFileText size={20} color="#7C3AED" />
          <Text size="xl" fw={700}>{allRecords.length}</Text>
          <Text size="xs" c="dimmed">Total Entries</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706', cursor: pendingReviewCount > 0 ? 'pointer' : undefined }}
          onClick={() => pendingReviewCount > 0 && navigate('/timelabor/review')}>
          <IconChecklist size={20} color="#D97706" />
          <Text size="xl" fw={700} c={pendingReviewCount > 0 ? '#D97706' : undefined}>{pendingReviewCount}</Text>
          <Text size="xs" c="dimmed">Pending Review</Text>
        </Paper>
      </SimpleGrid>

      {/* Compliance Alerts */}
      {complianceData.total > 0 && (
        <Paper p="md" withBorder mb="lg" style={{ borderLeft: '4px solid #D97706' }}>
          <Group justify="space-between" mb="xs">
            <Group gap={8}>
              <IconShieldCheck size={18} color="#D97706" />
              <Text fw={600} c="#92400E">
                Compliance Alerts: {complianceData.total} entries with PAPPG eligibility flags
              </Text>
            </Group>
            <Button variant="light" color="orange" size="xs"
              onClick={() => navigate('/timelabor/timeentry')}>
              View Flagged Entries
            </Button>
          </Group>
          <Group gap={12}>
            {Object.values(complianceData.byCodes).map(({ count, label }) => (
              <Badge key={label} size="sm" variant="light" color="yellow">
                {count}x {label}
              </Badge>
            ))}
          </Group>
        </Paper>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        {/* Recent Time Entries */}
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Recent Time Entries</Text>
          {recentEntries.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No time entries recorded yet.</Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Employee</Table.Th>
                  <Table.Th>Project</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Hours</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Cost</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentEntries.map(r => (
                  <Table.Tr key={r.Rid}>
                    <Table.Td>{r.workDate}</Table.Td>
                    <Table.Td>{r.employeeName}</Table.Td>
                    <Table.Td style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Text size="sm" c={r.projectId ? undefined : 'dimmed'}>
                        {r.projectId ? (projectLookup.get(r.projectId) || r.projectId) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(pf(r.hoursRegular) + pf(r.hoursOvertime)).toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {formatCurrency(r.totalCost)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* Hours by Employee */}
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Hours by Employee (All Time)</Text>
          {hoursByEmployee.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No hours logged yet.</Text>
          ) : (
            <Stack gap={6}>
              {hoursByEmployee.map(([name, hrs]) => (
                <div key={name}>
                  <Group justify="space-between" mb={2}>
                    <Text size="xs" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Text>
                    <Text size="xs" fw={600}>{hrs.toFixed(1)}h</Text>
                  </Group>
                  <div style={{ background: '#e5e7eb', borderRadius: 4, height: 12 }}>
                    <div style={{
                      width: `${Math.max((hrs / maxHours) * 100, 2)}%`,
                      background: TEAL,
                      borderRadius: 4,
                      height: 12,
                    }} />
                  </div>
                </div>
              ))}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>
    </div>
  );
}
